import React, { useCallback, useEffect, useRef, useState } from "react";
import { ListItem } from "../../interfaces/ScrollableList";

interface ScrollableListProps<T extends ListItem> {
  renderItem: (item: T, index: number) => React.ReactNode;
  filterListItems(linesToBeFiltered: T[]): T[];
  listItems: T[];
  itemHeight: number;
  containerHeight: number;
}

const ScrollableLargeList = <T extends ListItem>(
  props: ScrollableListProps<T>
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [startIndex, setStartIndex] = useState(0);

  const buffer = 20;
  const itemsPerPage = 100;

  const [displayListItems, setDisplayListItems] = useState<T[]>([]);

  // render event
  useEffect(() => {
    const displayLines = props.filterListItems(props.listItems);
    setDisplayListItems(displayLines);
  }, [props.listItems]);

  const throttledScroll = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      const newStartIndex = Math.max(
        0,
        Math.floor(scrollTop / props.itemHeight) - buffer
      );
      setStartIndex(newStartIndex);
    }
  }, [props.itemHeight, buffer]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let timeout: number | null = null;

    const onScroll = () => {
      if (timeout !== null) return;
      timeout = window.setTimeout(() => {
        throttledScroll();
        timeout = null;
      }, 50); // Throttle scroll to 50ms
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [throttledScroll]);

  const endIndex = Math.min(startIndex + itemsPerPage, displayListItems.length);
  const visibleItems = displayListItems.slice(startIndex, endIndex);

  const topPadding = startIndex * props.itemHeight;
  const bottomPadding =
    (displayListItems.length + 1 - endIndex + buffer) * props.itemHeight;

  return (
    <div
      id="scrollable-large-list"
      ref={containerRef}
      className="parts-list-container large-list"
      style={{
        height: `${props.containerHeight}px`,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch", // Mobile momentum scroll
      }}
    >
      <div
        style={{
          paddingTop: topPadding,
          paddingBottom: bottomPadding,
          marginLeft: "2vw",
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {visibleItems.map((item, i) => props.renderItem(item, startIndex + i))}
      </div>
    </div>
  );
};
export default ScrollableLargeList;
